// components/PositionSizer.jsx
import React, { useState } from 'react';
import '../../styles/PositionSizer.css';

export const PositionSizer = ({ onCalculate, portfolioId }) => {
  const [formData, setFormData] = useState({
    accountSize: 100000,
    riskPercent: 2,
    entryPrice: 150.00,
    stopLoss: 140.00,
    riskRewardRatio: 3,
    maxPortfolioAllocation: 20
  });

  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await onCalculate({
      ...formData,
      portfolioId
    });

    // Calculate position sizing
    const riskAmount = (formData.accountSize * formData.riskPercent) / 100;
    const riskPerShare = formData.entryPrice - formData.stopLoss;
    const shares = Math.floor(riskAmount / riskPerShare);
    const positionValue = shares * formData.entryPrice;
    const portfolioAllocation = (positionValue / formData.accountSize) * 100;
    const targetPrice = formData.entryPrice + (riskPerShare * formData.riskRewardRatio);
    const potentialProfit = shares * (targetPrice - formData.entryPrice);

    setResult({
      ...response,
      timestamp: new Date().toLocaleTimeString(),
      shares,
      positionValue,
      riskAmount,
      portfolioAllocation,
      targetPrice,
      potentialProfit,
      riskPerShare,
      isWithinLimits: portfolioAllocation <= formData.maxPortfolioAllocation
    });
  };

  return (
    <div className="panel position-sizer">
      <div className="panel-header">
        <h2>Position Size Calculator</h2>
        <span className="api-endpoint">POST /rms/position/size</span>
      </div>

      <div className="panel-content">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="accountSize">Account Size ($)</label>
              <input
                type="number"
                id="accountSize"
                name="accountSize"
                value={formData.accountSize}
                onChange={handleChange}
                min="100"
                step="1000"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="riskPercent">Risk per Trade (%)</label>
              <div className="range-input">
                <input
                  type="range"
                  id="riskPercent"
                  name="riskPercent"
                  min="0.5"
                  max="5"
                  step="0.5"
                  value={formData.riskPercent}
                  onChange={handleChange}
                />
                <span className="range-value">{formData.riskPercent}%</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="entryPrice">Entry Price ($)</label>
              <input
                type="number"
                id="entryPrice"
                name="entryPrice"
                step="0.01"
                value={formData.entryPrice}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="stopLoss">Stop Loss ($)</label>
              <input
                type="number"
                id="stopLoss"
                name="stopLoss"
                step="0.01"
                value={formData.stopLoss}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="riskRewardRatio">Risk/Reward Ratio</label>
              <div className="range-input">
                <input
                  type="range"
                  id="riskRewardRatio"
                  name="riskRewardRatio"
                  min="1"
                  max="5"
                  step="0.5"
                  value={formData.riskRewardRatio}
                  onChange={handleChange}
                />
                <span className="range-value">1:{formData.riskRewardRatio}</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="maxPortfolioAllocation">Max Allocation (%)</label>
              <div className="range-input">
                <input
                  type="range"
                  id="maxPortfolioAllocation"
                  name="maxPortfolioAllocation"
                  min="1"
                  max="50"
                  step="1"
                  value={formData.maxPortfolioAllocation}
                  onChange={handleChange}
                />
                <span className="range-value">{formData.maxPortfolioAllocation}%</span>
              </div>
            </div>
          </div>

          <div className="quick-stats">
            <div className="stat-card">
              <div className="stat-label">Risk Amount</div>
              <div className="stat-value">
                ${((formData.accountSize * formData.riskPercent) / 100).toFixed(2)}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Risk per Share</div>
              <div className="stat-value">
                ${(formData.entryPrice - formData.stopLoss).toFixed(2)}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Target Price</div>
              <div className="stat-value">
                ${(formData.entryPrice + ((formData.entryPrice - formData.stopLoss) * formData.riskRewardRatio)).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              ⚖️ Calculate Position Size
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setFormData({
              accountSize: 250000,
              riskPercent: 1.5,
              entryPrice: 320.75,
              stopLoss: 300.00,
              riskRewardRatio: 3.5,
              maxPortfolioAllocation: 15
            })}>
              Load Example
            </button>
          </div>
        </form>

        {result && (
          <div className="position-results">
            <div className="results-header">
              <h3>Position Size Results</h3>
              <span className="timestamp">{result.timestamp}</span>
            </div>

            <div className="position-metrics">
              <div className="metric-row">
                <div className="metric-item">
                  <span className="metric-label">Shares to Buy</span>
                  <span className="metric-value large">{result.shares}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Position Value</span>
                  <span className="metric-value">${result.positionValue.toFixed(2)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Risk Amount</span>
                  <span className="metric-value danger">${result.riskAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="allocation-meter">
                <div className="meter-header">
                  <span>Portfolio Allocation</span>
                  <span>{result.portfolioAllocation.toFixed(1)}%</span>
                </div>
                <div className="meter-bar">
                  <div 
                    className="meter-fill"
                    style={{ width: `${Math.min(result.portfolioAllocation, 100)}%` }}
                    data-status={result.isWithinLimits ? 'within' : 'exceeded'}
                  />
                  <div 
                    className="meter-limit"
                    style={{ left: `${formData.maxPortfolioAllocation}%` }}
                  />
                </div>
                <div className="meter-labels">
                  <span>0%</span>
                  <span>Max: {formData.maxPortfolioAllocation}%</span>
                  <span>100%</span>
                </div>
                {!result.isWithinLimits && (
                  <div className="warning-alert">
                    ⚠️ Position exceeds maximum allocation limit
                  </div>
                )}
              </div>

              <div className="profit-loss-projections">
                <h4>Profit & Loss Projections</h4>
                <div className="projections-grid">
                  <div className="projection-card">
                    <div className="projection-label">Target Price</div>
                    <div className="projection-value">${result.targetPrice.toFixed(2)}</div>
                    <div className="projection-change positive">
                      +{((result.targetPrice - formData.entryPrice) / formData.entryPrice * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="projection-card">
                    <div className="projection-label">Potential Profit</div>
                    <div className="projection-value positive">
                      ${result.potentialProfit.toFixed(2)}
                    </div>
                    <div className="projection-change positive">
                      +{(result.potentialProfit / result.positionValue * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="projection-card">
                    <div className="projection-label">Stop Loss</div>
                    <div className="projection-value">${formData.stopLoss.toFixed(2)}</div>
                    <div className="projection-change negative">
                      -{((formData.entryPrice - formData.stopLoss) / formData.entryPrice * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="projection-card">
                    <div className="projection-label">Risk/Reward</div>
                    <div className="projection-value">
                      1:{formData.riskRewardRatio}
                    </div>
                    <div className="projection-change">
                      {result.riskAmount > 0 ? 
                        `Reward ${(result.potentialProfit / result.riskAmount).toFixed(1)}x risk` : 
                        'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="risk-summary">
                <h4>Risk Summary</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Account Risk</span>
                    <span className="summary-value">{formData.riskPercent}%</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Risk per Share</span>
                    <span className="summary-value">${result.riskPerShare.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Max Loss</span>
                    <span className="summary-value danger">${result.riskAmount.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Within Limits</span>
                    <span className={`summary-value ${result.isWithinLimits ? 'success' : 'danger'}`}>
                      {result.isWithinLimits ? 'Yes ✓' : 'No ✗'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-primary">
                💾 Save Calculation
              </button>
              <button className="btn btn-secondary">
                📋 Copy Results
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};